import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { formatMinorAmount, getMinorUnitsSync } from '@sharecost/shared';
import type { BalanceBucket } from '@/api/statistics';
import { useChartTheme } from '@/hooks/useChartTheme';
import { useTranslation } from 'react-i18next';

interface BalanceOverTimeChartProps {
  data: BalanceBucket[];
  currency: string;
}

function CustomTooltip({ active, payload, label, currency }: any) {
  if (!active || !payload?.length) return null;
  const minorUnits = getMinorUnitsSync(currency);
  const value = payload[0].value as number;
  return (
    <div className="rounded-lg bg-surface-primary px-3 py-2 shadow-lg ring-1 ring-border-primary">
      <p className="text-xs font-medium text-text-tertiary">{label}</p>
      <p className={`text-sm font-semibold ${value >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
        {formatMinorAmount(value, currency, minorUnits)}
      </p>
    </div>
  );
}

export function BalanceOverTimeChart({ data, currency }: BalanceOverTimeChartProps) {
  const { t } = useTranslation();
  const minorUnits = getMinorUnitsSync(currency);
  const ct = useChartTheme();
  const formatTick = (value: number) =>
    formatMinorAmount(value, currency, minorUnits, undefined, true);

  if (data.length === 0) {
    return (
      <div className="flex h-[280px] items-center justify-center text-sm text-text-quaternary">
        {t('web:balanceOverTimeChart.noBalanceData')}
      </div>
    );
  }

  const hasNegative = data.some(d => d.balance < 0);
  const lineColor = hasNegative ? '#ef4444' : '#22c55e';
  const fillColor = hasNegative ? '#ef4444' : '#22c55e';

  return (
    <div className="rounded-xl bg-surface-primary p-5 shadow-sm ring-1 ring-border-primary">
      <h3 className="mb-4 text-sm font-semibold text-text-primary">{t('web:balanceOverTimeChart.balanceOverTime')}</h3>
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={data} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={ct.grid} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: ct.tick }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: ct.tick }}
            axisLine={false}
            tickLine={false}
            width={80}
            tickFormatter={formatTick}
          />
          <Tooltip content={<CustomTooltip currency={currency} />} cursor={{ stroke: ct.cursorStroke }} />
          <ReferenceLine y={0} stroke={ct.referenceLine} strokeDasharray="3 3" />
          <Area
            type="monotone"
            dataKey="balance"
            stroke={lineColor}
            fill={fillColor}
            fillOpacity={0.15}
            strokeWidth={2}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
